export function isNotNullish<T>(value: T): value is NonNullable<T> {
  return value !== null && typeof value !== 'undefined';
}

export type TypeMap = {
  string: string;
  number: number;
  bigint: bigint;
  boolean: boolean;
  symbol: symbol;
  undefined: undefined;
  object: object | null;
  date: Date;
  function: (...args: unknown[]) => unknown;
};

/**
 * Checks if the value is of the specified types.
 *
 * Acts as a type guard.
 *
 * @example
 * ```ts
 * const value: undefined | null | string | number = '123';
 * if (isTypeOf(value, ['string', 'number'])) {
 *   // value is now of type string or number
 * }
 * ```
 */
export function isTypeOf<T extends keyof TypeMap>(value: unknown, types: T[]): value is TypeMap[T] {
  if (value instanceof Date && types.includes('date' as T)) {
    return true;
  }
  return (types as string[]).includes(typeof value);
}

/**
 * Type guard for checking if a value is in an array.
 *
 * @example
 * ```ts
 * const arr = [1, 'a', true];
 * const value = 1 as unknown;
 * if (isInArray(arr, value)) {
 *   // value is now of type number | boolean | string
 * }
 * ```
 */
export function isInArray<T extends readonly unknown[]>(
  array: T,
  value: unknown
): value is T[number] {
  return array.includes(value as T[number]);
}

/**
 * Type guard for checking if a key is in an object.
 *
 * @example
 * ```ts
 * const obj = { a: 1, b: 2, c: 3 };
 * const key = 'a' as unknown;
 * if (isKeyOf(obj, key)) {
 *   // key is now of type 'a' | 'b' | 'c'
 * }
 * ```
 */
export function isKeyOf<T extends Record<string, unknown>>(
  object: T,
  key: unknown
): key is keyof T {
  return String(key) in object;
}
